import { useTable } from '@refinedev/antd';
import { useQuery } from '@tanstack/react-query';
import { Form, SelectProps } from 'antd';
import { FormProps } from 'antd/lib';
import { type Key, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCountryContext } from '@/contexts/CountryContext';
import { ShippingLabel } from '@/model/Logistic/ShippingLabel/ShippingLabel';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';
import { Country } from '@/utils/phoneNumbers';

import useDebouncedValue from './useDebouncedValue';
import { useSelectScrollSearch } from './useSelectScrollSearch';
import { useSelectPropsWithInitial } from './useSelectWithInitialValue';

interface UseLogisticAddressFormProps {
  formProps: FormProps;
  initialValuesModified?: any;
  initialValuesSetted?: boolean;
  setSaveAddress?: (value: boolean) => void;
}

const BRASIL_CEP_LENGTH = 8;
// Metadata: compara valores simples y soporta objetos labelInValue sin caer en "[object Object]".
const toComparable = (value?: unknown) => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  const option = value as { id?: unknown; value?: unknown; key?: unknown };
  const optionValue = option?.id ?? option?.value ?? option?.key;

  return typeof optionValue === 'string' || typeof optionValue === 'number'
    ? String(optionValue)
    : '';
};

const toText = (value?: unknown) => toComparable(value).trim().replace(/\s+/g, ' ').toLowerCase();
const toPostalCode = (value?: unknown) => toComparable(value).replace(/\D/g, '');
const initialCepState = {
  loading: false,
  location: undefined as any,
  error: false,
  canEditLocationManually: false,
  canEditAddressManually: false,
};
const isSavedAddressEqualToForm = (
  savedAddress: ShippingLabel,
  values: {
    pais?: unknown;
    provincia?: unknown;
    localidad?: unknown;
    address?: unknown;
    addressNumber?: unknown;
    addressComplement?: unknown;
    email?: unknown;
    phone?: unknown;
    name?: unknown;
    postalCode?: unknown;
  },
) =>
  toComparable(values.pais) === toComparable(savedAddress.localidad?.provincia?.pais?.id) &&
  toComparable(values.provincia) === toComparable(savedAddress.localidad?.provincia?.id) &&
  toComparable(values.localidad) === toComparable(savedAddress.localidad?.id) &&
  toText(values.address) === toText(savedAddress.address) &&
  toText(values.addressNumber) === toText(savedAddress.address_number) &&
  toText(values.addressComplement) === toText(savedAddress.address_complement) &&
  toText(values.email) === toText(savedAddress.email) &&
  toText(values.phone) === toText(savedAddress.phone_number) &&
  toText(values.name) === toText(savedAddress.contact) &&
  toPostalCode(values.postalCode) === toPostalCode(savedAddress.postal_code);

/**
 * Centraliza la gestión de direcciones guardadas, localidades y reseteos dependientes.
 */
export const useLogisticAddressForm = ({
  formProps,
  initialValuesModified,
  initialValuesSetted,
  setSaveAddress,
}: UseLogisticAddressFormProps) => {
  const { packageRepository } = useRepositoriesContext();
  const { isBrasilSelected } = useCountryContext();
  const { form } = formProps;
  const [selectedAddress, setSelectedAddress] = useState<ShippingLabel | undefined>();
  const company = Form.useWatch('company', form);
  const pais = Form.useWatch('receiver_pais', form);
  const provincia = Form.useWatch('receiver_provincia', form);
  const localidad = Form.useWatch('receiver_localidad', form);
  const address = Form.useWatch('receiver_address', form);
  const addressNumber = Form.useWatch('receiver_address_number', form);
  const addressComplement = Form.useWatch('receiver_address_complement', form);
  const email = Form.useWatch('receiver_email', form);
  const phone = Form.useWatch('receiver_phone', form);
  const name = Form.useWatch('receiver_name', form);
  const postalCode = Form.useWatch('receiver_postal_code', form);
  const debouncedPostalCode = useDebouncedValue(postalCode, 500);
  const normalizedPostalCode = useMemo(
    () => toPostalCode(debouncedPostalCode),
    [debouncedPostalCode],
  );
  const isCepLookupEnabled = isBrasilSelected && normalizedPostalCode.length >= BRASIL_CEP_LENGTH;
  const [cepLookupState, setCepLookupState] = useState(initialCepState);
  // Metadata: opciones dependientes compartidas por dirección de envío y dirección de retorno.
  const { selectProps: paisesSelectProps } = useSelectScrollSearch(
    {
      resource: 'paises',
      optionLabel: (item: any) => `${item.nombre}`,
    },
    { searchField: 'nombre' },
  );
  const { selectProps: provinciasSelectProps } = useSelectScrollSearch(
    {
      resource: 'provincias',
      optionLabel: (item: any) => `${item.nombre}`,
      filters: [
        {
          value: pais,
          operator: 'eq' as const,
          field: 'pais',
        },
      ],
      queryOptions: {
        enabled: !!pais && !paisesSelectProps.loading,
      },
    },
    { searchField: 'nombre' },
  );
  const { selectProps: localidadesSelectProps } = useSelectScrollSearch(
    {
      resource: 'localidades',
      optionLabel: (item: any) => `${item.nombre}`,
      filters: [
        {
          value: provincia,
          operator: 'eq' as const,
          field: 'provincia',
        },
      ],
      queryOptions: {
        enabled: !provinciasSelectProps.loading && !!pais && !!provincia,
      },
    },
    {
      saveItem: true,
      searchField: 'nombre',
    },
  );
  const paisesSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: paisesSelectProps,
    resource: 'paises',
    initialValue: initialValuesModified?.receiver_pais,
    labelKey: 'nombre',
    valueKey: 'id',
  });
  const provinciasSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: provinciasSelectProps,
    resource: 'provincias',
    initialValue: initialValuesModified?.receiver_provincia,
    labelKey: 'nombre',
    valueKey: 'id',
  });
  const localidadesSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: localidadesSelectProps,
    resource: 'localidades',
    initialValue: initialValuesModified?.receiver_localidad,
    labelKey: 'nombre',
    valueKey: 'id',
    getOption: (data: any) => ({
      label: data.nombre,
      value: data.id,
    }),
  });
  // Metadata para diferenciar reseteos manuales de actualizaciones programáticas.
  const previousLocationRef = useRef({
    pais: undefined,
    provincia: undefined,
    localidad: undefined,
    postalCode: undefined,
  });
  const skipNextLocationResetRef = useRef(false);
  const { tableProps } = useTable<ShippingLabel>({
    resource: RESOURCES.SHIPPING_LABELS,
    pagination: { pageSize: 20 },
    meta: {
      empresa: company,
    },
  });
  const countryCode = useMemo(() => Country[pais as keyof typeof Country], [pais]);
  const { dataSource, loading: savedAddressesLoading } = tableProps;
  const setProgrammaticFields = useCallback(
    (values: Record<string, unknown>) => {
      if (!form) return;

      // Metadata: evita que los reseteos dependientes borren valores cargados desde CEP o direcciones guardadas.
      skipNextLocationResetRef.current = true;
      form.setFieldsValue(values);
    },
    [form],
  );
  const resetCepState = useCallback(() => {
    setCepLookupState(initialCepState);
  }, []);
  const clearCepResolvedFields = useCallback(() => {
    // Metadata: borra sólo los datos que se autocompletan desde CEP para no conservar resultados anteriores.
    setProgrammaticFields({
      receiver_provincia: undefined,
      receiver_localidad: undefined,
      receiver_address: undefined,
    });
  }, [setProgrammaticFields]);
  const provinciasOptions = useMemo(() => {
    const options = provinciasSelectPropsWithInitials.options || [];
    const cepProvincia = cepLookupState.location?.provincia;

    if (cepProvincia && !options.some(option => option.value === cepProvincia.id)) {
      return [{ value: cepProvincia.id, label: cepProvincia.nombre }, ...options];
    }

    return options;
  }, [cepLookupState.location?.provincia, provinciasSelectPropsWithInitials.options]);
  const localidadesOptions = useMemo(() => {
    const options = localidadesSelectPropsWithInitials.options || [];
    const cepCiudad = cepLookupState.location?.ciudad;
    const optionsWithCepCiudad =
      cepCiudad && !options.some(option => option.value === cepCiudad.id)
        ? [
            {
              value: cepCiudad.id,
              label: cepCiudad.nombre,
              postal_code: postalCode,
            },
            ...options,
          ]
        : options;

    if (
      selectedAddress &&
      !optionsWithCepCiudad.some(option => option.value === selectedAddress.localidad.id)
    ) {
      return [
        {
          value: selectedAddress.localidad.id,
          label: selectedAddress.localidad.nombre,
          postal_code: selectedAddress.postal_code,
        },
        ...optionsWithCepCiudad,
      ];
    }

    return optionsWithCepCiudad;
  }, [
    cepLookupState.location?.ciudad,
    localidadesSelectPropsWithInitials.options,
    postalCode,
    selectedAddress,
  ]);

  useEffect(() => {
    if (savedAddressesLoading || !dataSource) return;

    const currentAddressValues = {
      pais,
      provincia,
      localidad,
      address,
      addressNumber,
      addressComplement,
      email,
      phone,
      name,
      postalCode,
    };
    // Metadata: sólo se guarda una dirección cuando el formulario tiene los datos mínimos del payload.
    const hasCurrentAddressRequiredFields =
      !!company &&
      !!pais &&
      !!provincia &&
      !!localidad &&
      !!toText(address) &&
      !!toText(email) &&
      !!toText(phone) &&
      !!toText(name) &&
      !!toPostalCode(postalCode);
    const matchingSavedAddress = dataSource.find(savedAddress =>
      isSavedAddressEqualToForm(savedAddress, currentAddressValues),
    );

    if (selectedAddress && selectedAddress.id !== matchingSavedAddress?.id) {
      setSelectedAddress(undefined);
    }

    // Metadata: el flag refleja si la dirección actual es nueva frente a todas las guardadas.
    setSaveAddress?.(hasCurrentAddressRequiredFields && !matchingSavedAddress);
  }, [
    address,
    addressComplement,
    addressNumber,
    company,
    dataSource,
    email,
    localidad,
    name,
    pais,
    phone,
    postalCode,
    provincia,
    savedAddressesLoading,
    selectedAddress,
    setSaveAddress,
  ]);

  useEffect(() => {
    if (initialValuesSetted && form) {
      previousLocationRef.current = {
        pais: form.getFieldValue('receiver_pais'),
        provincia: form.getFieldValue('receiver_provincia'),
        localidad: form.getFieldValue('receiver_localidad'),
        postalCode: form.getFieldValue('receiver_postal_code'),
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValuesSetted]);

  useEffect(() => {
    if (!form || !initialValuesSetted) {
      return;
    }

    if (skipNextLocationResetRef.current) {
      previousLocationRef.current = { pais, provincia, localidad, postalCode };
      skipNextLocationResetRef.current = false;

      return;
    }

    const previousLocation = previousLocationRef.current;

    if (isBrasilSelected && postalCode !== previousLocation.postalCode) {
      form.setFieldsValue({
        receiver_provincia: undefined,
        receiver_localidad: undefined,
        receiver_address: undefined,
      });
      resetCepState();
      previousLocationRef.current = {
        pais,
        provincia: undefined,
        localidad: undefined,
        postalCode,
      };

      return;
    }

    // Si cambia el país, provincia/localidad/código postal dejan de ser confiables.
    if (pais !== previousLocation.pais) {
      form.setFieldsValue({
        receiver_provincia: undefined,
        receiver_localidad: undefined,
        receiver_postal_code: undefined,
      });
      previousLocationRef.current = {
        pais,
        provincia: undefined,
        localidad: undefined,
        postalCode: undefined,
      };

      return;
    }

    // Si cambia la provincia, localidad/código postal deben elegirse nuevamente.
    if (provincia !== previousLocation.provincia) {
      form.setFieldsValue({
        receiver_localidad: undefined,
        ...(!isBrasilSelected ? { receiver_postal_code: undefined } : {}),
      });
      previousLocationRef.current = {
        pais,
        provincia,
        localidad: undefined,
        postalCode: isBrasilSelected ? postalCode : undefined,
      };

      return;
    }

    if (!isBrasilSelected && localidad !== previousLocation.localidad) {
      if (!localidad && !isBrasilSelected) {
        form.setFieldsValue({
          receiver_postal_code: undefined,
        });
      }

      previousLocationRef.current = { pais, provincia, localidad, postalCode };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais, provincia, localidad, postalCode, initialValuesSetted, isBrasilSelected]);

  /**
   * Solicitud de CEP
   */
  const {
    data: cepData,
    error: cepError,
    isLoading: cepLoading,
  } = useQuery({
    queryKey: ['locationByCep', normalizedPostalCode],
    queryFn: () => packageRepository.getLocationFromCEP(normalizedPostalCode),
    enabled: isCepLookupEnabled,
    retry: false,
    staleTime: 0,
  });

  useEffect(() => {
    if (!isBrasilSelected) {
      resetCepState();

      return;
    }

    if (!isCepLookupEnabled) {
      resetCepState();
      clearCepResolvedFields();

      return;
    }

    if (cepLoading) {
      setCepLookupState({ ...initialCepState, loading: true });

      return;
    }

    if (cepError || !cepData) {
      setCepLookupState({
        ...initialCepState,
        error: !!cepError,
      });
      clearCepResolvedFields();

      return;
    }

    setCepLookupState({
      ...initialCepState,
      location: cepData,
      canEditAddressManually: !cepData?.direccion,
    });
    setProgrammaticFields({
      receiver_provincia: cepData?.provincia?.id,
      receiver_localidad: cepData?.ciudad?.id,
      receiver_address: cepData?.direccion,
    });
  }, [
    isBrasilSelected,
    isCepLookupEnabled,
    cepData,
    cepError,
    cepLoading,
    clearCepResolvedFields,
    resetCepState,
    setProgrammaticFields,
  ]);

  const handleSavedAddressChange = useCallback(
    (_selectedKeys: Key[], selectedRows: ShippingLabel[]) => {
      const selectedRow = selectedRows[0];

      if (!selectedRow) {
        setSelectedAddress(undefined);
        setSaveAddress?.(true);

        return;
      }

      if (form) {
        setProgrammaticFields({
          receiver_address: selectedRow.address,
          receiver_address_number: selectedRow.address_number,
          receiver_address_complement: selectedRow.address_complement,
          receiver_email: selectedRow.email,
          receiver_phone: selectedRow.phone_number,
          receiver_name: selectedRow.contact,
          receiver_pais: selectedRow.localidad.provincia.pais.id,
          receiver_provincia: selectedRow.localidad.provincia.id,
          receiver_localidad: selectedRow.localidad.id,
          receiver_postal_code: selectedRow.postal_code,
        });
      }

      setSelectedAddress(selectedRow);
      setSaveAddress?.(false);
    },
    [form, setProgrammaticFields, setSaveAddress],
  );
  const handleLocalidadChange = useCallback(
    (value: SelectProps['value'], option: any) => {
      localidadesSelectPropsWithInitials.onChange?.(value, option);

      if (form && initialValuesSetted && option) {
        form.setFieldsValue({ receiver_postal_code: option.item?.postal_code });
      }
    },
    [form, initialValuesSetted, localidadesSelectPropsWithInitials],
  );
  const enableBrazilManualLocation = useCallback(() => {
    setCepLookupState(current => ({ ...current, canEditLocationManually: true }));
  }, []);

  return {
    company,
    pais,
    provincia,
    selectedAddress,
    tableProps,
    dataSource,
    paisesSelectProps: paisesSelectPropsWithInitials,
    provinciasSelectProps: { ...provinciasSelectPropsWithInitials, options: provinciasOptions },
    localidadesSelectProps: localidadesSelectPropsWithInitials,
    countryCode,
    isBrasilSelected,
    localidadesOptions,
    handleSavedAddressChange,
    handleLocalidadChange,
    cepLookupState,
    enableBrazilManualLocation,
  };
};
